import React from "react";
import { FaGlasses, FaHistory, FaUserCircle } from "react-icons/fa";
import {
  HiCalendar,
  HiOutlineViewBoards,
  HiPencil,
  HiTrash,
} from "react-icons/hi";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import DashboardStatusBadge from "../../../components/common/DashboardStatusBadge";
import RiskStatusDropdown from "../../../components/common/RiskStatusDropdown";
import TruncatedText from "../../../components/common/TruncatedText";
import Cost from "../../../components/Cost";
import ProgressCircle from "../../../components/features/ProgressCircle/ProgressCircle";
import { defaultUserNames } from "../../../constants/defaultUsers";
import {
  dashboardStatusHexColors,
  getDashboardStatusColor,
  getDashboardStatusLabel,
} from "../../../constants/integrationTypes";
import { getRiskDetailsFromStats } from "../../../helpers/riskStatusHelper";
import { useResourceInfo } from "../../../hooks/useResourceInfo";
import { EntityCountDisplay } from "../../../pages/Projects/sections/Project/sections/EntityCountDisplay";
import WorkItemOptions from "../../../pages/Projects/sections/Project/sections/WorkItemOptions";
import { resetRoadmap, setSingleRoadmap } from "../../../redux/slices/roadmap";
import { resetTimelineState } from "../../../redux/slices/timeline";
import { StrategyEntityItemType } from "../../../services/strategyMap";
import TooltipWrapper from "../../../shared/atoms/TooltipWrapper";
import {
  getChildEntityBreakdown,
  getChildItemCount,
  getEntityTypeDisplay,
} from "../../../utils/entityDisplayUtils";
import { toRelativeTime } from "../../../utils/timeUtils";
import { WorkUnitResourceMembers } from "./WorkUnitResourceMember/WorkUnitResourceMembers";
import StrategyMapSidePanelItem from "../../StrategymapSidePanel/components/StrategyMapSidePanelItem/StrategyMapSidePanelItem";
import { SAMPLE_ROADMAP_DATA } from "../../../misc/sample_datas/roadmapdata";

interface StrategyMapThumbnailProps {
  data: StrategyEntityItemType;
  onEdit: (data: any) => void;
  onDelete?: (data: any) => void;
  minimzed?: boolean;
  onClick?: (data: any) => void;
}

const WorkUnitMiniViewCard: React.FC<StrategyMapThumbnailProps> = ({
  data,
  onEdit,
  minimzed = false,
  onDelete,
  onClick,
}) => {
  const { getResourceName } = useResourceInfo();

  const getDisplayCreatorName = (id: string | undefined) => {
    if (!id) return "";
    if (parseInt(id) <= 0) {
      return defaultUserNames[id] || "System";
    }
    return getResourceName(id);
  };

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const progress = data.progress || 0;
  const startDate = data.start_date || "";
  const endDate = data.end_date || "";

  const getTimeline = () => {
    if (!startDate || !endDate) return "No date range";
    try {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const day1 = start.getDate();
      const month1 = start.toLocaleString("en-US", { month: "short" });
      const year1 = start.getFullYear().toString().slice(-2);
      const day2 = end.getDate();
      const month2 = end.toLocaleString("en-US", { month: "short" });
      const year2 = end.getFullYear().toString().slice(-2);
      return `${day1} ${month1}, ${year1} - ${day2} ${month2}, ${year2}`;
    } catch (e) {
      return "Invalid date range";
    }
  };

  const timeline = getTimeline();

  // Determine entity info - hardcoding for Strategy Map as it's specific
  const entityInfo = getEntityTypeDisplay(
    data?.entity_type ?? "strategy_map",
    data.type,
  );

  const IconComponent = entityInfo.icon;

  const countEntities = data?.entities?.length || 0;
  const count = countEntities;

  const statusColor =
    data.status && dashboardStatusHexColors[(data.status || "").toLowerCase()]
      ? dashboardStatusHexColors[(data.status || "").toLowerCase()]
      : "#6366f1";

  const isSimPending = data.is_sim_pending;

  
  return (
    <div
      className="group relative w-full hover-card mt-0 !py-0"
      data-onboarding-card={data.status === "at_risk" ? "at-risk" : "other"}
      data-onboarding-entity-name={data.name}
      data-onboarding-entity-id={data.id}
    >
      <div className="block cursor-pointer">
        {/* FIXED HEIGHT CARD - 170px */}
        <div
          className={`flex flex-col  

            transition-all duration-150  overflow-hidden  `}
          style={{ borderLeftColor: statusColor }}
        >
          {/* Content with reduced top padding */}
          <div
            className="flex-1   flex flex-col relative"
            onClick={() => {
              onClick?.(data);
            }}
          >
            {/* ROW 1: Header -> Icon + Type + Stage */}
            <div className="unit-header flex items-center py-0">
              {/* Entity Icon */}
              <div
                className={`rounded-lg flex items-center justify-center flex-shrink-0`}
              >
                <div className="flex-shrink-0 flex items-center">
                  <ProgressCircle
                    progress={data.progress || 0}
                    size={40}
                    strokeWidth={4}
                    color={statusColor}
                    showPercentage={true}
                  />
                </div>
              </div>

              {/* Unit Header Content */}

              <div className="unit-header-content flex flex-col py-2 ml-2 w-full">
                <div className="unit-title overflow-hidden w-full">
                  <TruncatedText
                    text={data.name || "Untitled Strategy Map"}
                    className="text-sm font-semibold text-gray-900 dark:text-white leading-tight truncate max-w-[70%]"
                    as="h3"
                    alwaysShowTooltip={false}
                  />
                </div>

                {/* Type + Count + Stage */}
                <div className="flex ">
                  <div className=" min-w-0 flex items-center overflow-hidden gap-1">
                    <IconComponent
                      className={`w-[12px] h-[12px] ${entityInfo.iconClass}`}
                    />
                    <span
                      className={`text-[10px] font-semibold uppercase ${entityInfo.labelClass} `}
                    >
                      {entityInfo.label}
                      {data.integration_data?.integration_type
                        ? ` (${data.integration_data?.integration_type})`
                        : ""}

                      {data.stage ? ` - ${data.stage}` : ""}
                    </span>
                  </div>

                  {getChildItemCount(data) > 0 && (
                    <EntityCountDisplay
                      count={getChildItemCount(data)}
                      childEntityTypes={data.child_entity_types}
                      entityType={data.entity_type}
                      className="text-[10px]"
                      showSeparator={true}
                      textSize="10px"
                    />
                  )}
                </div>
              </div>
            </div>


              {/* <div className="flex flex-col py-2 ">

                <span className="text-xs text-gray-500 dark:text-slate-400">All Items</span>

                <StrategyMapSidePanelItem
                  projectData={SAMPLE_ROADMAP_DATA}
                  isSimulation={false}
                  onTimelineClick={() => {}}
                  onItemClick={() => {}}
                />
            </div> */}
            {/* ROW 4: Footer */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkUnitMiniViewCard;
